import { useEffect, useLayoutEffect } from 'react';
import { TextAreaRef } from 'antd/es/input/TextArea';
import {
  useConversationInputDraft,
  useSetConversationInputDraft,
  useStateSafeConversationQuotedMsgDraft,
} from 'hooks/business/conversationDraft';
import { useSetConversationToggle } from 'hooks/business/conversationsHooks/useConversationHotKey';
import insertAtCursor from 'utils/insertAtCursor';
import { useMemoizedFn } from 'ahooks';

/**
 * 消息编辑器输入与草稿
 * 单一职责：管理输入内容、引用消息草稿、光标处插入、同步空态 toggle
 */
export function useMessageEditorDraft(
  conversationId: string,
  textareaRef: React.RefObject<TextAreaRef | null>,
) {
  const input = useConversationInputDraft(conversationId);
  const setInput = useSetConversationInputDraft();
  const setConversationToggle = useSetConversationToggle('messageEditorEmpty');
  const quotedMsg = useStateSafeConversationQuotedMsgDraft(conversationId);

  const trimInput = input?.trim() ?? '';

  useEffect(() => {
    setConversationToggle(trimInput ? false : true);
  }, [trimInput, setConversationToggle]);

  // 引用消息变化时聚焦到输入框末尾
  useLayoutEffect(() => {
    if (quotedMsg) {
      setTimeout(() => {
        textareaRef.current?.focus({ cursor: 'end' });
      }, 600);
    }
  }, [quotedMsg, textareaRef]);

  const handleTextChange = useMemoizedFn((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(conversationId, e.target.value);
  });

  const insertText = useMemoizedFn((text: string) => {
    const { current: textarea } = textareaRef;
    if (textarea && text) {
      const { resizableTextArea } = textarea;
      if (resizableTextArea) {
        const { textArea } = resizableTextArea;
        const nextValue = insertAtCursor(textArea, text);
        setInput(conversationId, nextValue);
      }
    }
  });

  return {
    input,
    setInput,
    quotedMsg,
    trimInput,
    handleTextChange,
    insertText,
  };
}
